import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import type { MineBetMode, MineGamePhase, MineGameResult, MineTile } from '@/types/mine'
import { AssetManager } from '../core/AssetManager'
import { ResultModal } from './ResultModal'
import { MINE_BOARD_SIZE, MINE_BOARD_COLUMNS } from '@/config/original'

const EXPLOSION_EFFECT_MS = 900

interface MineBoardProps {
	revealedTiles: MineTile[]
	autoSelectedPoints: number[]
	phase: MineGamePhase
	betMode: MineBetMode
	isResultVisible: boolean
	currentResult: MineGameResult
	currencyCode: string
	onTileClick: (point: number) => void
}

function getTileState(point: number, revealedTiles: MineTile[], autoSelectedPoints: number[], betMode: MineBetMode) {
	const revealedTile = revealedTiles.find((tile) => tile.point === point)
	const isAutoSelected = betMode === 'auto' && autoSelectedPoints.includes(point)

	return { revealedTile, isAutoSelected }
}

export function MineBoard({ revealedTiles, autoSelectedPoints, phase, betMode, isResultVisible, currentResult, currencyCode, onTileClick }: MineBoardProps) {
	const [explosionState, setExplosionState] = useState<{ point: number; status: 'playing' | 'done' } | null>(null)
	const previousTilesRef = useRef(revealedTiles)
	const explosionTimerRef = useRef<number | null>(null)
	const explosionRunRef = useRef(0)

	useLayoutEffect(() => {
		const previousTiles = previousTilesRef.current
		const newlyRevealedBombs = revealedTiles.filter((tile) => {
			const previousTile = previousTiles.find((previous) => previous.point === tile.point)

			return tile.isRevealed && tile.content === 'bomb' && previousTile?.content !== 'bomb'
		})

		previousTilesRef.current = revealedTiles

		// Prefer an auto-selected bomb (auto-bet loss), then the pending tile the player just clicked.
		// Do not fall back to an arbitrary newly revealed bomb — cashout/auto also reveal the full board.
		const triggeredBomb =
			newlyRevealedBombs.find((tile) => autoSelectedPoints.includes(tile.point)) ??
			newlyRevealedBombs.find((tile) => {
				const previousTile = previousTiles.find((previous) => previous.point === tile.point)
				return previousTile != null && !previousTile.isRevealed
			})

		if (!triggeredBomb) {
			return
		}

		explosionRunRef.current += 1
		const runId = explosionRunRef.current
		setExplosionState({ point: triggeredBomb.point, status: 'playing' })

		if (explosionTimerRef.current !== null) {
			window.clearTimeout(explosionTimerRef.current)
		}

		explosionTimerRef.current = window.setTimeout(() => {
			if (runId !== explosionRunRef.current) {
				return
			}

			setExplosionState({ point: triggeredBomb.point, status: 'done' })
			explosionTimerRef.current = null
		}, EXPLOSION_EFFECT_MS)
	}, [autoSelectedPoints, revealedTiles])

	useEffect(() => {
		if (revealedTiles.length === 0) {
			explosionRunRef.current += 1
			setExplosionState(null)

			if (explosionTimerRef.current !== null) {
				window.clearTimeout(explosionTimerRef.current)
				explosionTimerRef.current = null
			}
		}
	}, [revealedTiles.length])

	useEffect(
		() => () => {
			if (explosionTimerRef.current !== null) {
				window.clearTimeout(explosionTimerRef.current)
			}
		},
		[],
	)

	const isAutoMode = betMode === 'auto'
	const canInteract = (phase === 'ready' && isAutoMode) || (phase === 'live' && !isAutoMode)

	return (
		<div
			className='relative h-full min-h-0 w-full overflow-hidden rounded-xl bg-cover bg-center bg-no-repeat'
			style={{ backgroundImage: `url(${AssetManager.getImageUrl('mineboardBg')})` }}
		>
			<div className='absolute inset-0 flex items-center justify-center p-3 sm:p-4'>
				<div className='grid aspect-square h-[90%] w-auto max-w-[90%] gap-1' style={{ gridTemplateColumns: `repeat(${MINE_BOARD_COLUMNS}, minmax(0, 1fr))` }}>
					{Array.from({ length: MINE_BOARD_SIZE }, (_, point) => {
						const { revealedTile, isAutoSelected } = getTileState(point, revealedTiles, autoSelectedPoints, betMode)
						const hasRevealedContent = Boolean(revealedTile?.isRevealed)
						const isPendingReveal = Boolean(revealedTile && !revealedTile.isRevealed)

						return (
							<button
								key={point}
								type='button'
								disabled={!canInteract}
								onClick={() => onTileClick(point)}
								className={cn(
									'relative aspect-square overflow-hidden rounded-md p-1 transition-colors',
									canInteract && 'active:animate-squeeze active:animate-duration-fast',
									hasRevealedContent && 'bg-secondary',
									isPendingReveal && 'bg-accent',
									!hasRevealedContent && !isPendingReveal && (isAutoSelected ? 'bg-primary' : 'bg-secondary hover:bg-accent'),
									isAutoSelected && 'ring-2 ring-primary',
								)}
							>
								{hasRevealedContent && revealedTile?.content === 'gem' ? (
									<img
										src={AssetManager.getImageUrl('gemIcon')}
										alt='Gem'
										className='h-full w-full animate-bounce-fade-in object-contain animate-duration-fast'
									/>
								) : null}

								{hasRevealedContent && revealedTile?.content === 'bomb' ? (
									<div className='relative h-full w-full animate-zoom-in animate-duration-fast'>
										<img src={AssetManager.getImageUrl('bombIcon')} alt='Bomb' className='relative z-0 h-full w-full object-contain' />
										{explosionState?.point === point && explosionState.status === 'playing' ? (
											<img
												src={AssetManager.getImageUrl('explosionEffect')}
												alt=''
												className='pointer-events-none absolute inset-0 z-10 h-full w-full scale-125 animate-pop object-contain mix-blend-screen'
											/>
										) : null}
									</div>
								) : null}
							</button>
						)
					})}
				</div>
			</div>

			<ResultModal visible={isResultVisible} result={currentResult} currencyCode={currencyCode} />
		</div>
	)
}
